import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const SIDE_IMG = "https://images.pexels.com/photos/34155535/pexels-photo-34155535.jpeg";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen grid md:grid-cols-2" data-testid="forgot-password-page">
      <div className="relative hidden md:block bg-primary overflow-hidden">
        <img src={SIDE_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-primary/50" />
        <div className="relative h-full flex flex-col justify-between p-12 text-primary-foreground">
          <span className="font-display text-2xl">Coastal Chemistry</span>
          <h2 className="font-display text-5xl font-light leading-[0.95] tracking-tight max-w-sm">A new key, quickly.</h2>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-24 md:py-0">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="w-full max-w-sm">
          <p className="label text-accent mb-4">/ Forgot password</p>
          <h1 className="font-display text-4xl font-light tracking-tight mb-10">Reset your access</h1>

          {sent ? (
            <div className="border border-border p-6 flex flex-col gap-4" data-testid="forgot-success">
              <div className="h-12 w-12 flex items-center justify-center bg-accent text-accent-foreground"><Check size={22} strokeWidth={1.5} /></div>
              <p className="text-sm text-foreground/80 leading-relaxed">
                If an account exists for <span className="font-mono">{email}</span>, we&apos;ve sent a reset link. Check your inbox &mdash; the link expires in 30 minutes.
              </p>
              <Link to="/login" className="label inline-flex items-center gap-2 border-b border-foreground pb-1 hover:border-accent hover:text-accent transition-colors self-start" data-testid="forgot-back-to-login">
                Back to sign in <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
            </div>
          ) : (
            <>
              {error && <p className="mb-6 text-sm text-destructive border border-destructive px-4 py-3" data-testid="forgot-error">{error}</p>}
              <form onSubmit={submit} className="space-y-6" data-testid="forgot-form">
                <div>
                  <label className="label text-muted-foreground block mb-2">Email</label>
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-accent transition-colors" data-testid="forgot-email" />
                  <p className="mt-2 text-xs text-muted-foreground">We&apos;ll email a secure link to choose a new password.</p>
                </div>
                <button type="submit" disabled={busy} className="w-full h-14 bg-primary text-primary-foreground label hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="forgot-submit">
                  {busy ? "Sending…" : "Send reset link"} <ArrowRight size={16} strokeWidth={1.5} />
                </button>
              </form>
            </>
          )}

          <p className="mt-8 text-sm text-muted-foreground">
            Remembered it? <Link to="/login" className="text-accent border-b border-accent" data-testid="to-login">Sign in</Link>
          </p>
        </motion.div>
      </div>
    </main>
  );
}
